/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col, Row } from "react-awesome-styled-grid";
import { If, Then } from "react-if";
import { useParams } from "react-router-dom";
import { Box, BrickLoader } from "@nordcloud/gnui";
import { ResourceStatus } from "~/generated/graphql";
import { ChangeDetails } from "~/components/ChangeDetails";
import { Details } from "~/components/Details";
import { ErrorWrap } from "~/components/Error";
import { LoaderWrap } from "~/components/Loader";
import { BorderWrapper } from "~/components/Styled";
import { LastPatchingStatusContainer } from "~/views/resources_v2/components/LastPatchingStatus";
import { ResourceDetailsBreadcrumbs } from "~/views/resources_v2/ResourceDetails/components/ResourceDetailsBreadcrumbs";
import { ResourceDetailsStatus } from "~/views/resources_v2/ResourceDetails/components/ResourceDetailsStatus";
import {
  DetailsColumnFirst,
  DetailsColumnSecond,
  HeaderPresetResource,
} from "~/views/resources_v2/ResourceDetails/presets";
import { isVirtualMachine } from "~/views/resources_v2/utils";
import { useGetCloudResource } from "../hooks";
import { ResourceCtxProvider } from "../ResourceProvider";
import { Tabs } from "./Tabs/Tabs";

type ResourceRouteParams = {
  resourceId: string;
};

export function ResourceDetailsV2() {
  const { resourceId } = useParams<ResourceRouteParams>();
  const { cloudResource, error, loading } = useGetCloudResource({ resourceId });
  const isVM = cloudResource ? isVirtualMachine(cloudResource) : false;

  return (
    <ResourceCtxProvider value={{ cloudResource }}>
      <Row>
        <Col>
          <ResourceDetailsBreadcrumbs
            title={cloudResource?.name ?? resourceId ?? ""}
            cloudResourceId={cloudResource?.id ?? ""}
          />
        </Col>
      </Row>
      <Box spacing="spacing02">
        <LoaderWrap loading={loading} inContent>
          <ErrorWrap error={error}>
            <Row>
              <Col xs={8} sm={8} md={5} lg={8}>
                <BorderWrapper
                  css={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ResourceDetailsStatus
                    provider={cloudResource?.provider}
                    status={isVM ? ResourceStatus.Online : undefined}
                    boxes
                  />
                  <div css={{ width: "100%" }}>
                    <Details details={HeaderPresetResource(cloudResource)} />
                  </div>
                </BorderWrapper>
                <Row>
                  <Col xs={4} sm={4} md={6} lg={6}>
                    <Details details={DetailsColumnFirst(cloudResource)} />
                  </Col>
                  <Col xs={7} sm={4} md={8} lg={6}>
                    <Details details={DetailsColumnSecond(cloudResource)} />
                  </Col>
                </Row>
              </Col>
              <Col xs={8} sm={8} md={3} lg={4}>
                <ChangeDetails
                  details={cloudResource?.changeDetails}
                  removeBorderBottomRadius={!isVM}
                />
                <If condition={isVM}>
                  <Then>
                    <LastPatchingStatusContainer
                      lastPatching={cloudResource?.lastPatching ?? undefined}
                    />
                  </Then>
                </If>
              </Col>
            </Row>
          </ErrorWrap>
        </LoaderWrap>
      </Box>
      <LoaderWrap loading={loading} Component={BrickLoader}>
        <Tabs />
      </LoaderWrap>
    </ResourceCtxProvider>
  );
}
